import { useCallback, useEffect, useRef, useState } from "react";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { useInitialDataOfSingleMedicalOrder } from "../../../../context/initialDataOfSingleMedicalOrder";
import { User } from "../../../../data-model/firebaseAuthModel";
import { GeneralOrderDetails } from "../../../../data-model/medicalPaOrdersModel";
import { EventsName } from "../../../../enums/eventsName";
import { SubmissionDataResponse } from "../../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { useIsPrimaryDiagnosisValidated } from "../../../../pages/singleOrderLayout/hooks/useIsPrimaryDiagnosisValidated";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { ClinicalDataTableProps, TableData } from "../types";
import {
  buildPrimaryIcdSavedAdditionalData,
  ClinicalDataAnalyticsContext,
} from "../utils/buildClinicalDataAnalytics";
import {
  handleSave,
  resolvePrimaryDiagnosisForSave,
} from "../utils/saveNewPrimaryDiagnosisCode";

/** Table state and context passed from {@link useClinicalDataTable}. */
export interface UseClinicalDataTableSaveInput {
  props: ClinicalDataTableProps;
  tableData: TableData[];
  selectedPrimaryCode: string;
  defaultPrimaryCode: string;
  manualPrimaryCode: string;
  selectedPrimaryCodeDescription: string;
  primaryIcdCodes: string[];
  dropdownOpts: { value: string; label: string }[];
  analyticsContext: ClinicalDataAnalyticsContext;
  submissionData: SubmissionDataResponse | null;
  fetchAll: (orderId: string, orgId: string) => Promise<void>;
  generalOrderDetails: { order: GeneralOrderDetails } | null;
  user: User | null;
  userReason: string;
  userSource: string;
  otherReason: string;
  otherSource: string;
}

/**
 * Owns save-side state and the primary Dx save handler for the modal.
 *
 * Derives whether primary Dx validation is required via
 * {@link useIsPrimaryDiagnosisValidated} and forwards that to
 * {@link handleSave}, which gates the validated-status update and analytics
 * event on it.
 *
 * @param input - Table state and context from {@link useClinicalDataTable}.
 * @return Save handler and saving flag.
 */
export const useClinicalDataTableSave = (
  input: UseClinicalDataTableSaveInput,
) => {
  const { oldPrimaryIcdCodes: oldPrimaryIcdCodesFromContext } =
    useInitialDataOfSingleMedicalOrder();
  const { filteredSiblingOrders } = useDataAccumulatorForSingleOrder();
  const [isSaving, setIsSaving] = useState(false);
  const isPrimaryDiagnosisValidated = useIsPrimaryDiagnosisValidated();
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  /**
   * Creates a new AbortController for each save operation.
   *
   * @return Fresh abort controller for the current save request.
   */
  const createNewAbortController = useCallback(() => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller;
  }, []);

  const onSave = useCallback(() => {
    const resolvedDiagnosis = resolvePrimaryDiagnosisForSave(
      input.selectedPrimaryCode,
      input.tableData,
      {
        manualPrimaryCode: input.manualPrimaryCode,
        manualPrimaryCodeDescription: input.selectedPrimaryCodeDescription,
      },
    );

    apiWorkerManager.enqueueAndProcess({
      patient_id: input.analyticsContext.patientId,
      event_name: EventsName.PRIMARY_ICD_SAVED,
      org_id: input.analyticsContext.orgId,
      order_id: input.analyticsContext.orderId,
      user_id: input.analyticsContext.userId,
      user_email: input.analyticsContext.userEmail,
      additional_data: buildPrimaryIcdSavedAdditionalData(
        input.analyticsContext,
        {
          defaultPrimaryCode: input.defaultPrimaryCode,
          newPrimaryIcdCode:
            resolvedDiagnosis?.icdCode ?? input.selectedPrimaryCode,
          regimenIcdState:
            input.submissionData?.submission?.regimen_icd_state ?? "",
          primaryIcdCodesCount: input.primaryIcdCodes.length,
          icdCodesChanged: (() => {
            const oldCodes = (oldPrimaryIcdCodesFromContext ?? []).join(",");
            const newCodes = input.primaryIcdCodes.join(",");
            return oldCodes !== newCodes;
          })(),
        },
      ),
    });

    const controller = createNewAbortController();
    const userReason = input.userReason?.trim() || undefined;
    const userSource = input.userSource?.trim() || undefined;
    const otherReason = input.otherReason?.trim() || undefined;
    const otherSource = input.otherSource?.trim() || undefined;

    // Preserve the selected dropdown values and send custom "Other" textarea
    // text in separate fields for the backend request mapper.
    const changeContext =
      userReason || userSource || otherReason || otherSource
        ? { userReason, userSource, otherReason, otherSource }
        : undefined;

    handleSave(
      input.submissionData?.submission?.primary_diagnosis_details ?? [],
      input.selectedPrimaryCode,
      input.tableData,
      controller.signal,
      input.generalOrderDetails?.order?.order_id ?? "",
      {
        onStart: () => setIsSaving(true),
        onFinish: () => setIsSaving(false),
        onSave: input.props.onSave,
        onError: (err) => console.error(err),
      },
      input.fetchAll,
      {
        manualPrimaryCode: input.manualPrimaryCode,
        manualPrimaryCodeDescription: input.selectedPrimaryCodeDescription,
      },
      { id: input.user?.id, email: input.user?.email },
      input.generalOrderDetails?.order,
      input.defaultPrimaryCode,
      input.dropdownOpts,
      input.props.screenName,
      isPrimaryDiagnosisValidated.isValidationRequired,
      changeContext,
      input.analyticsContext,
      filteredSiblingOrders?.siblings?.map((sibling) => sibling.order_id) ?? [],
    );
  }, [
    input,
    oldPrimaryIcdCodesFromContext,
    createNewAbortController,
    isPrimaryDiagnosisValidated.isValidationRequired,
    filteredSiblingOrders,
  ]);

  return { isSaving, onSave };
};
